import { useContext, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { ToastContext } from "../../../hooks/context";
import { returnStock } from "../../../services/inventory";
import { cleanObject, getDateInUTCFormat } from "../../../helpers";
import { useParams } from "react-router-dom";
import { useGetAllChartOfAccounts } from "../../../hooks/api/queries";

export const useReturnStock = ({
  closeWidget = () => {},
  defaultOrderId = null,
}) => {
  const params = useParams();
  const triggerToast = useContext(ToastContext);
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    order_id: defaultOrderId || "",
    store_id: params?.store_id || "",
    remarks: "",
    items: [],
    return_to_store_wallet: false,
    ledger_account_id: "",
    return_date: "",
    return_time: "",
    available_discount: 0,
    total_remaining_credit: 0,
    outstanding_balance: 0,
    include_delivery_fee: false,
    refundable_delivery_fee: 0,
    delivery_fee_amount: 0,
    total_returned_delivery_fee: 0,
    remaining_delivery_fee: 0,
    include_service_charge: false,
    refundable_service_charge: 0,
    service_charge_amount: 0,
    total_returned_service_charge: 0,
    remaining_service_charge: 0,
  });

  const { isLoading: isLoadingBankAccountsList, data: bankAccounts } =
    useGetAllChartOfAccounts({
      sub_account_type: "cash_and_bank",
    });

  const isOrderItemsSelected =
    formData?.items
      ?.map((item) => item?.isReturning === true)
      ?.filter((item) => item === true)?.length >= 1;

  const returnStockMutation = useMutation({
    mutationFn: (data) => returnStock(data),
    onSuccess: () => {},
  });

  const handleReturnStock = () => {
    const customerId = formData?.customer?.customerId;
    if (formData?.return_to_store_wallet && !customerId)
      return triggerToast("Please select the customer you wish to credit");

    const deliveryFeeRefund = Number(formData?.refundable_delivery_fee || 0);
    const remainingDeliveryFee = Number(formData?.remaining_delivery_fee || 0);

    if (
      formData?.include_delivery_fee &&
      (deliveryFeeRefund <= 0 || deliveryFeeRefund > remainingDeliveryFee)
    ) {
      return triggerToast("Enter a valid delivery fee refund amount");
    }

    const serviceChargeRefund = Number(
      formData?.refundable_service_charge || 0,
    );
    const remainingServiceCharge = Number(
      formData?.remaining_service_charge || 0,
    );

    if (
      formData?.include_service_charge &&
      (serviceChargeRefund <= 0 || serviceChargeRefund > remainingServiceCharge)
    ) {
      return triggerToast("Enter a valid service charge refund amount");
    }

    returnStockMutation.mutate(
      cleanObject(
        {
          ...formData,
          return_date:
            formData?.return_date === ""
              ? formData?.return_date
              : getDateInUTCFormat({
                  date: formData?.return_date,
                  time: formData?.return_time,
                }),
          items: formData?.items?.filter((item) => item?.isReturning !== false),
          remarks: formData.remarks ? formData.remarks : undefined,
          customer_id: customerId,
          refundable_delivery_fee: formData?.include_delivery_fee
            ? Number(deliveryFeeRefund.toFixed(2))
            : 0,
          refundable_service_charge: formData?.include_service_charge
            ? Number(serviceChargeRefund.toFixed(2))
            : 0,
        },
        [
          "customer",
          "return_time",
          "include_delivery_fee",
          "delivery_fee_amount",
          "total_returned_delivery_fee",
          "remaining_delivery_fee",
          "include_service_charge",
          "service_charge_amount",
          "total_returned_service_charge",
          "remaining_service_charge",
        ],
      ),
      {
        onSuccess: (res) => {
          triggerToast(
            res?.data?.detail || "Product returned successfully",
            "success",
          );
          queryClient.invalidateQueries(["getReturnsList"]);
          queryClient.invalidateQueries(["getReturnRequestsList"]);
          queryClient.invalidateQueries(["getStoreWalletTransactionDetails"]);
          closeWidget();
        },
      },
    );
  };

  return {
    formData,
    isReturningStock: returnStockMutation.isLoading,
    stores: storeList,
    isOrderItemsSelected,
    isLoadingBankAccountsList,
    bankAccounts,
    handleReturnStock,
    setFormData,
  };
};
